/**
 * Missions data: state.ext.missions from the store (GET /api/state, then SSE 'ext'), and the
 * calls that change it. Live mode calls the hub and then re-reads GET /api/missions so the page
 * never waits on the stream; demo mode (?mock=1) runs the same rules in the browser.
 */
import { useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { buildMissionPrompt, newPrefixedId } from '@orgus/protocol';
import type {
  ApprovalsState,
  AutopilotPatch,
  AutopilotStatus,
  BacklogItem,
  Id,
  ItemInput,
  ItemPatch,
  Mission,
  MissionInput,
  MissionLaunch,
  MissionPatch,
  MissionStartRequest,
  MissionStartResponse,
  MissionsState,
  SessionStartParams,
} from '@orgus/protocol';
import { ApiError, api, apiGet, runAction, toUiError } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { useCosts } from '../costs/useCosts.ts';
import { resolveMissionBlocks } from '../approvals/blocked.ts';
import { mockSetAutopilot } from './autopilot.ts';
import { MISSIONS_EXT, missionRows, missionsSlice, viewOf } from './model.ts';
import type { MissionRow } from './model.ts';
import {
  MockRefusal,
  createMockMissions,
  mockCreateItem,
  mockCreateMission,
  mockDeleteItem,
  mockDeleteMission,
  mockRecordLaunch,
  mockUpdateItem,
  mockUpdateMission,
} from './mock.ts';

const isMock = () => useOrgus.getState().mode === 'mock';
const current = (): MissionsState => missionsSlice(useOrgus.getState().ext) ?? { items: [], missions: [] };
const put = (data: MissionsState) => useOrgus.getState().applyExt({ ext: MISSIONS_EXT, type: 'state', data });

/** The demo's mirror of the hub: closing a mission marks its blocking requests handled (ADR 0056 section 6). */
function closeBlocks(m: Mission): void {
  const st = useOrgus.getState();
  const cur = st.ext.approvals as ApprovalsState | undefined;
  if (!cur) return;
  const next = resolveMissionBlocks(cur, m, 'owner', Date.now());
  if (next !== cur) st.applyExt({ ext: 'approvals', type: 'state', data: next });
}

export type MissionsPhase = 'loading' | 'ready' | 'error' | 'unavailable';

/**
 * The missions slice and where it stands. 'unavailable': the hub answered but has no missions
 * module (an older hub). Demo mode seeds demo missions on first use.
 */
export function useMissionsState(): { state: MissionsState | null; phase: MissionsPhase } {
  const { slice, phase, mode } = useOrgus(useShallow((s) => ({ slice: missionsSlice(s.ext), phase: s.phase, mode: s.mode })));
  useEffect(() => {
    if (mode === 'mock' && phase === 'ready' && !slice) put(createMockMissions(Date.now()));
  }, [mode, phase, slice]);
  if (phase === 'loading') return { state: null, phase: 'loading' };
  if (phase === 'error') return { state: null, phase: 'error' };
  if (!slice) return { state: null, phase: mode === 'mock' ? 'loading' : 'unavailable' };
  return { state: slice, phase: 'ready' };
}

/**
 * What a mission view derives from: the store, with the costs module's state as the Spend page
 * sees it (demo spend in mock mode, which never lands in state.ext), so a mission's Spend is in
 * dollars wherever the Spend page has dollars.
 */
function useDeriveSource() {
  const src = useOrgus(useShallow((s) => ({ sessions: s.sessions, machines: s.machines, ext: s.ext })));
  const costs = useCosts();
  return useMemo(() => (costs && src.ext.costs !== costs ? { ...src, ext: { ...src.ext, costs } } : src), [src, costs]);
}

/** Every mission with its live derived view. Recomputes when sessions, machines or module data change. */
export function useMissionRows(state: MissionsState | null, now: number): MissionRow[] {
  const src = useDeriveSource();
  // `now` moves in coarse steps (useNow), so this recomputes at most that often plus on data changes.
  return useMemo(() => (state ? missionRows(state, src, now) : []), [state, src, now]);
}

export function useMissionView(mission: Mission | undefined, now: number) {
  const src = useDeriveSource();
  return useMemo(() => (mission ? viewOf(mission, src, now) : undefined), [mission, src, now]);
}

/** Re-read the slice from the hub (after a change, or on Retry). */
export async function refreshMissions(): Promise<void> {
  if (isMock()) return;
  put(await apiGet<MissionsState>('/api/missions'));
}

function refusal(e: unknown, what: string): never {
  if (e instanceof MockRefusal) throw new ApiError({ what, why: e.message, next: 'Fix it and try again.' });
  throw e;
}

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function live<T>(path: string, method: 'POST' | 'PUT' | 'PATCH' | 'DELETE', body?: unknown): Promise<T> {
  const out = await api<T>(path, { method, body: body ?? (method === 'DELETE' ? undefined : {}) });
  await refreshMissions().catch(() => undefined);
  return out;
}

// ---------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------

export async function createItem(input: ItemInput): Promise<BacklogItem> {
  if (!isMock()) return live<BacklogItem>('/api/missions/items', 'POST', input);
  await delay(250);
  try {
    const r = mockCreateItem(current(), input, Date.now());
    put(r.state);
    return r.item;
  } catch (e) {
    return refusal(e, 'The item was not created.');
  }
}

export async function updateItem(id: Id, patch: ItemPatch): Promise<BacklogItem> {
  if (!isMock()) return live<BacklogItem>(`/api/missions/items/${encodeURIComponent(id)}`, 'PATCH', patch);
  await delay(150);
  try {
    const r = mockUpdateItem(current(), id, patch, Date.now());
    put(r.state);
    return r.item;
  } catch (e) {
    return refusal(e, 'The item was not changed.');
  }
}

export async function deleteItem(id: Id): Promise<void> {
  if (!isMock()) return void (await live(`/api/missions/items/${encodeURIComponent(id)}`, 'DELETE'));
  await delay(200);
  try {
    put(mockDeleteItem(current(), id));
  } catch (e) {
    refusal(e, 'That item was not deleted.');
  }
}

// ---------------------------------------------------------------------------
// Missions
// ---------------------------------------------------------------------------

export async function createMission(input: MissionInput): Promise<Mission> {
  if (!isMock()) return live<Mission>('/api/missions', 'POST', input);
  await delay(200);
  try {
    const r = mockCreateMission(current(), input, 'owner', Date.now());
    put(r.state);
    return r.mission;
  } catch (e) {
    return refusal(e, 'The mission was not created.');
  }
}

export async function updateMission(id: Id, patch: MissionPatch): Promise<Mission> {
  if (!isMock()) return live<Mission>(`/api/missions/${encodeURIComponent(id)}`, 'PATCH', patch);
  await delay(150);
  try {
    const r = mockUpdateMission(current(), id, patch, 'owner', Date.now());
    put(r.state);
    if (patch.closed) closeBlocks(r.mission);
    return r.mission;
  } catch (e) {
    return refusal(e, 'The mission was not changed.');
  }
}

export async function deleteMission(id: Id): Promise<void> {
  if (!isMock()) return void (await live(`/api/missions/${encodeURIComponent(id)}`, 'DELETE'));
  await delay(150);
  try {
    put(mockDeleteMission(current(), id));
  } catch (e) {
    refusal(e, 'That mission was not deleted.');
  }
}

/**
 * Start a session for the mission. Resolves with the hub's answer; throws ApiError when the
 * machine refused or could not be reached (the failed start is still recorded on the mission).
 */
export async function startMission(id: Id, req: MissionStartRequest): Promise<MissionStartResponse> {
  if (!isMock()) {
    let res: MissionStartResponse;
    try {
      res = await api<MissionStartResponse>(`/api/missions/${encodeURIComponent(id)}/start`, { method: 'POST', body: req, timeoutMs: 45_000 });
    } finally {
      await refreshMissions().catch(() => undefined);
    }
    if (!res.ok) throw new ApiError({ what: 'The machine did not start the session.', why: res.message ?? 'It did not say why.', next: 'Fix the cause on the machine, then start again. The attempt is kept on the mission.' });
    return res;
  }
  const s = current();
  const m = s.missions.find((x) => x.id === id);
  if (!m) throw new ApiError({ what: 'The mission was not started.', why: 'That mission is not known to this hub.', next: 'Reload Missions.' });
  const item = m.itemId ? s.items.find((i) => i.id === m.itemId) ?? null : null;
  const model = req.model === undefined ? m.model : req.model;
  const params: SessionStartParams = {
    ...(req.checkoutId ? { checkoutId: req.checkoutId } : { cwd: req.cwd! }),
    prompt: buildMissionPrompt(m, item, req.note, [], { worktree: !!req.newWorktree }),
    mode: req.mode ?? 'headless',
    ...(model ? { model } : {}),
    ...(req.newWorktree ? { newWorktree: req.newWorktree } : {}),
  };
  const at = Date.now();
  let ok = true;
  let message: string | undefined;
  let sessionId: Id | null = null;
  try {
    const r = await runAction(req.machineId, 'session.start', params);
    const data = (r.data ?? {}) as { sessionId?: unknown };
    sessionId = typeof data.sessionId === 'string' ? data.sessionId : null;
  } catch (e) {
    ok = false;
    message = toUiError(e).why;
  }
  const checkout = req.checkoutId ? useOrgus.getState().checkouts[req.checkoutId] : undefined;
  const launch: MissionLaunch = {
    id: newPrefixedId('evt', at),
    machineId: req.machineId,
    at,
    by: 'owner',
    mode: req.mode ?? 'headless',
    model: model ?? null,
    checkoutId: req.checkoutId ?? null,
    cwd: checkout?.path ?? req.cwd ?? null,
    worktree: null,
    sessionId,
    result: ok ? 'ok' : 'error',
    ...(message ? { message } : {}),
  };
  const r = mockRecordLaunch(current(), id, launch, { ...('budget' in req ? { budget: req.budget ?? null } : {}), ...(model !== undefined ? { model: model ?? null } : {}) }, Date.now());
  put(r.state);
  if (!ok) throw new ApiError({ what: 'The machine did not start the session.', why: message ?? 'It did not say why.', next: 'Fix the cause on the machine, then start again. The attempt is kept on the mission.' });
  return { ok: true, mission: r.mission, launch };
}

/**
 * Demo mode only: what the hub's Autopilot tick does for one new item when Autopilot is on for its
 * project, start a mission for it. A live hub starts it on its own tick, so this does nothing there.
 */
export async function demoAutopilotTake(item: BacklogItem): Promise<void> {
  if (!isMock()) return;
  const s = current();
  // ?autopilot=1 plays a project with Autopilot on (the e2e of ORGU-307).
  const on = s.autopilot?.find((a) => a.projectId === item.projectId)?.settings.on || new URLSearchParams(location.search).has('autopilot');
  if (!on) return;
  await delay(400);
  const r = mockCreateMission(current(), { projectId: item.projectId, itemId: item.id, title: item.title, goal: `Every acceptance criterion of ${item.key} is met.` }, 'autopilot', Date.now());
  put({ ...r.state, missions: r.state.missions.map((m) => (m.id === r.mission.id ? { ...m, state: 'running', autopilot: true } : m)) });
}

/** Change a project's Autopilot (ADR 0073): the switch, limits, kinds and permission mode. */
export async function setAutopilot(projectId: Id, patch: AutopilotPatch): Promise<AutopilotStatus> {
  if (!isMock()) return live<AutopilotStatus>(`/api/missions/autopilot/${encodeURIComponent(projectId)}`, 'PUT', patch);
  await delay(150);
  const next = mockSetAutopilot(current(), projectId, patch, Date.now());
  put(next);
  return next.autopilot!.find((a) => a.projectId === projectId)!;
}

/**
 * Pause or resume every mission (ADR 0103): nothing new starts and queued work waits; running
 * sessions finish their current turn. Demo mode flips the slice.
 */
export async function setMissionsPaused(on: boolean): Promise<void> {
  if (!isMock()) {
    put(await api<MissionsState>('/api/missions/pause', { method: 'PUT', body: { on } }));
    return;
  }
  await delay(150);
  put({ ...current(), pause: on ? { by: 'owner', at: Date.now() } : null });
}

/** Pause or resume one mission (ADR 0103). */
export async function setMissionPaused(id: Id, on: boolean): Promise<void> {
  if (!isMock()) {
    // Two literal paths, so the client-routes contract sees each route.
    await live<Mission>(on ? `/api/missions/${encodeURIComponent(id)}/pause` : `/api/missions/${encodeURIComponent(id)}/resume`, 'POST');
    return;
  }
  await delay(150);
  const st = current();
  put({ ...st, missions: st.missions.map((m) => (m.id === id ? { ...m, paused: on ? { by: 'owner', at: Date.now() } : null, updatedAt: Date.now() } : m)) });
}

/** Stop every live session the mission started, then close it as stopped. */
export async function stopMission(id: Id): Promise<{ ok: boolean; message: string }> {
  if (!isMock()) {
    const r = await live<{ ok: boolean; message: string }>(`/api/missions/${encodeURIComponent(id)}/stop`, 'POST');
    if (!r.ok) throw new ApiError({ what: 'Some sessions did not stop.', why: r.message, next: 'Check those machines, then try again.' });
    return r;
  }
  const st = useOrgus.getState();
  const m = current().missions.find((x) => x.id === id);
  if (!m) throw new ApiError({ what: 'The mission was not stopped.', why: 'That mission is not known to this hub.', next: 'Reload Missions.' });
  const view = viewOf(m, st, Date.now());
  const liveOnes = view.sessions.filter((s) => s.session && s.session.status !== 'ended');
  for (const s of liveOnes) await runAction(s.machineId, 'session.stop', { sessionId: s.sessionId! });
  const closed = mockUpdateMission(current(), id, { closed: 'stopped' }, 'owner', Date.now());
  put(closed.state);
  closeBlocks(closed.mission);
  return { ok: true, message: liveOnes.length ? `Stopped ${liveOnes.length} ${liveOnes.length === 1 ? 'session' : 'sessions'}.` : 'No session was running. The mission is closed as stopped.' };
}
